import React, { useEffect, useMemo, useRef, useState } from 'react'
import styles from './index.module.scss'
import DocumentsEmptyIcon from '../../../../assets/icon/DocumentsEmptyIcon'
import CheckIcon from '../../../../assets/icon/CheckIcon'
import ProjectDeleteModal from '../../../../components/ProjectControls/ProjectDeleteModal'
import DocumentService from '../../../../services/document.service'
import ProjectService from '../../../../services/project.service'
import { Project, ProjectDocument } from '../../../../types'
import SearchSmallIcon from '../../../../assets/icon/SearchSmallIcon'
import { buildProjectDocumentLink, getProjectDocumentName } from '../../../../utils/projectDocument'
import DocumentRow from './DocumentRow'
import {
    buildDocumentRenameValue,
    extractProjects,
    getMovePopoverPosition,
    isArchivedDocument,
    isDeletedDocument,
    isOwnedByCurrentUser,
    splitDocumentNameParts,
} from './utils'

interface DocumentsViewProps {
    projectId: string
    documents: ProjectDocument[]
    currentUserIds?: Array<string | number>
    currentUserEmails?: string[]
    isLoading: boolean
    showOnlyMyDocuments: boolean
    showArchivedDocuments: boolean
    highlightedDocumentId?: string | null
    onLinkCopied: () => void
    onDocumentUpdated: (document: ProjectDocument | null, options?: { remove?: boolean }) => void
    onDocumentArchived?: (document: ProjectDocument, undo: () => Promise<void>) => void
    onUploadClick: () => void
    onRefresh: () => Promise<void>
}

export default function DocumentsView({
    projectId,
    documents,
    currentUserIds = [],
    currentUserEmails = [],
    isLoading,
    showOnlyMyDocuments,
    showArchivedDocuments,
    highlightedDocumentId,
    onLinkCopied,
    onDocumentUpdated,
    onDocumentArchived,
    onUploadClick,
    onRefresh,
}: DocumentsViewProps) {
    const [selectedIds, setSelectedIds] = useState<Array<string | number>>([])
    const [deleteDocument, setDeleteDocument] = useState<ProjectDocument | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const [renamingId, setRenamingId] = useState<string | number | null>(null)
    const [renameName, setRenameName] = useState('')
    const [moveDocument, setMoveDocument] = useState<ProjectDocument | null>(null)
    const [moveSearch, setMoveSearch] = useState('')
    const [moveProjects, setMoveProjects] = useState<Project[]>([])
    const [isMoveProjectsLoading, setIsMoveProjectsLoading] = useState(false)
    const [isMoving, setIsMoving] = useState(false)
    const [moveAnchorRect, setMoveAnchorRect] = useState<DOMRect | null>(null)
    const movePopoverRef = useRef<HTMLDivElement>(null)

    const filteredDocuments = useMemo(() => {
        return documents.filter((document) => {
            if (isDeletedDocument(document)) {
                return false
            }

            const shouldShowArchivedOnly = showArchivedDocuments && !showOnlyMyDocuments

            if (shouldShowArchivedOnly && !isArchivedDocument(document)) {
                return false
            }

            if (!showArchivedDocuments && isArchivedDocument(document)) {
                return false
            }

            if (showOnlyMyDocuments && !isOwnedByCurrentUser(document, currentUserIds, currentUserEmails)) {
                return false
            }

            return true
        })
    }, [currentUserEmails, currentUserIds, documents, showArchivedDocuments, showOnlyMyDocuments])

    const visibleSelectedCount = filteredDocuments.filter((document) => (
        selectedIds.some((currentId) => String(currentId) === String(document.id))
    )).length
    const isAllSelected = filteredDocuments.length > 0 && visibleSelectedCount === filteredDocuments.length
    const isSomeSelected = visibleSelectedCount > 0 && !isAllSelected
    const isEmpty = filteredDocuments.length === 0
    const emptyTitle = showArchivedDocuments && !showOnlyMyDocuments
        ? 'У вас еще нет архивированных документов'
        : 'У вас еще нет документов'

    const filteredMoveProjects = useMemo(() => {
        return moveProjects.filter((project) => (
            String(project.id) !== String(projectId)
            && project.name.toLowerCase().includes(moveSearch.toLowerCase())
        ))
    }, [moveProjects, moveSearch, projectId])

    useEffect(() => {
        setSelectedIds((prev) => prev.filter((currentId) => (
            filteredDocuments.some((document) => String(document.id) === String(currentId))
        )))
    }, [filteredDocuments])

    useEffect(() => {
        if (!highlightedDocumentId) {
            return
        }

        const rowElement = document.querySelector(`[data-document-row-id="${highlightedDocumentId}"]`)

        if (rowElement instanceof HTMLElement) {
            rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
    }, [filteredDocuments, highlightedDocumentId, isLoading])

    useEffect(() => {
        if (!moveDocument) {
            return
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (
                movePopoverRef.current
                && !movePopoverRef.current.contains(event.target as Node)
            ) {
                setMoveDocument(null)
                setMoveSearch('')
                setMoveAnchorRect(null)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)

        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [moveDocument])

    useEffect(() => {
        if (!moveDocument || moveProjects.length > 0) {
            return
        }

        let isMounted = true

        const loadProjects = async () => {
            setIsMoveProjectsLoading(true)

            try {
                const projectList = await ProjectService.list()

                if (!isMounted) {
                    return
                }

                setMoveProjects(extractProjects(projectList))
            } catch (error) {
                if (isMounted) {
                    setMoveProjects([])
                }
                console.error('Failed to load projects for move:', error)
            } finally {
                if (isMounted) {
                    setIsMoveProjectsLoading(false)
                }
            }
        }

        void loadProjects()

        return () => {
            isMounted = false
        }
    }, [moveDocument, moveProjects.length])

    const handleSelectAll = () => {
        if (isAllSelected || isSomeSelected) {
            setSelectedIds([])
            return
        }

        setSelectedIds(filteredDocuments.map((document) => document.id))
    }

    const handleToggleItem = (id: string | number) => {
        setSelectedIds((prev) => (
            prev.some((currentId) => String(currentId) === String(id))
                ? prev.filter((currentId) => String(currentId) !== String(id))
                : [...prev, id]
        ))
    }

    const startRename = (document: ProjectDocument) => {
        setRenamingId(document.id)
        setRenameName(splitDocumentNameParts(document).baseName)
    }

    const cancelRename = () => {
        setRenamingId(null)
        setRenameName('')
    }

    const confirmRename = async () => {
        if (renamingId == null || !renameName.trim()) {
            return
        }

        const currentDocument = documents.find((document) => String(document.id) === String(renamingId))

        if (!currentDocument) {
            cancelRename()
            return
        }

        const { error, value } = buildDocumentRenameValue(currentDocument, renameName)

        if (error || !value) {
            window.alert(error || 'Не удалось переименовать документ.')
            return
        }

        try {
            await DocumentService.rename(projectId, renamingId, value)
            await onRefresh()
            cancelRename()
        } catch (error) {
            console.error('Failed to rename document:', error)
        }
    }

    const handleCopyDocumentLink = async (document: ProjectDocument) => {
        await navigator.clipboard.writeText(buildProjectDocumentLink(projectId, document.id))
        onLinkCopied()
    }

    const handleMoveClick = (document: ProjectDocument, rect: DOMRect) => {
        setMoveDocument(document)
        setMoveAnchorRect(rect)
        setMoveSearch('')
    }

    const handleArchiveToggle = async (document: ProjectDocument) => {
        const wasArchived = isArchivedDocument(document)
        const updatedDocument = wasArchived
            ? await DocumentService.unarchive(projectId, document.id)
            : await DocumentService.archive(projectId, document.id)

        setSelectedIds((prev) => prev.filter((id) => String(id) !== String(document.id)))
        onDocumentUpdated(updatedDocument)

        if (!wasArchived && onDocumentArchived) {
            const undo = async () => {
                try {
                    const restored = await DocumentService.unarchive(projectId, document.id)
                    onDocumentUpdated(restored)
                } catch (error) {
                    console.error('Failed to undo archive:', error)
                }
            }
            onDocumentArchived(document, undo)
        }
    }

    const handleMoveToProject = async (targetProject: Project) => {
        if (!moveDocument) {
            return
        }

        setIsMoving(true)

        try {
            await DocumentService.move(projectId, moveDocument.id, {
                project_id: targetProject.id,
                target_project_id: targetProject.id,
            })
            onDocumentUpdated(moveDocument, { remove: true })
            setMoveDocument(null)
            setMoveSearch('')
            setMoveAnchorRect(null)
            await onRefresh()
        } catch (error) {
            console.error('Failed to move document:', error)
        } finally {
            setIsMoving(false)
        }
    }

    const confirmDelete = async () => {
        if (!deleteDocument) return

        setIsDeleting(true)

        try {
            await DocumentService.delete(projectId, deleteDocument.id)
            onDocumentUpdated(deleteDocument, { remove: true })
            setDeleteDocument(null)
            setSelectedIds((prev) => prev.filter((id) => String(id) !== String(deleteDocument.id)))
            await onRefresh()
        } catch (error) {
            console.error('Failed to delete document:', error)
        } finally {
            setIsDeleting(false)
        }
    }

    const movePopoverPosition = getMovePopoverPosition(moveAnchorRect)

    return (
        <div className={styles.documentsView}>
            {isLoading ? (
                <div className={styles.documentsView__status}>Загружаем документы...</div>
            ) : isEmpty ? (
                <div className={styles.documentsView__empty}>
                    <div className={styles.documentsView__emptyIcon}>
                        <DocumentsEmptyIcon />
                    </div>
                    <div className={styles.documentsView__emptyTitle}>{emptyTitle}</div>
                    <button type="button" className={styles.documentsView__emptyBtn} onClick={onUploadClick}>
                        Загрузить документ
                    </button>
                </div>
            ) : (
                <div className={styles.documentsView__table}>
                    <div className={styles.documentsView__header}>
                        <div className={styles.documentsView__cellCheckbox}>
                            <div
                                className={`${styles.documentsView__checkbox} ${isAllSelected ? styles.documentsView__checkboxActive : ''} ${isSomeSelected ? styles.documentsView__checkboxPartial : ''}`}
                                onClick={handleSelectAll}
                            >
                                {isAllSelected && <CheckIcon />}
                                {isSomeSelected && <div className={styles.documentsView__checkboxDash} />}
                            </div>
                        </div>
                        <div className={styles.documentsView__cellNameHeader}>Название</div>
                        <div className={styles.documentsView__cellOwnerHeader}>Владелец</div>
                        <div className={styles.documentsView__cellDateHeader}>Дата создания</div>
                        <div className={styles.documentsView__cellActionsHeader} />
                    </div>
                    <div className={styles.documentsView__body}>
                        {filteredDocuments.map((document) => (
                            <DocumentRow
                                key={document.id}
                                document={document}
                                projectId={projectId}
                                isSelected={selectedIds.some((id) => String(id) === String(document.id))}
                                isBusy={isDeleting || isMoving}
                                isHighlighted={String(highlightedDocumentId) === String(document.id)}
                                isRenaming={String(renamingId) === String(document.id)}
                                renameName={renameName}
                                onToggle={() => handleToggleItem(document.id)}
                                onRenameStart={startRename}
                                onRenameChange={setRenameName}
                                onRenameConfirm={confirmRename}
                                onRenameCancel={cancelRename}
                                onCopyLink={handleCopyDocumentLink}
                                onMoveClick={handleMoveClick}
                                onArchiveToggle={handleArchiveToggle}
                                onRefresh={onRefresh}
                                onDeleteClick={setDeleteDocument}
                            />
                        ))}
                    </div>
                </div>
            )}

            {moveDocument && (
                <div
                    ref={movePopoverRef}
                    className={styles.documentsView__movePopover}
                    style={{ left: movePopoverPosition.left, top: movePopoverPosition.top }}
                >
                    <div className={styles.documentsView__moveSearch}>
                        <div className={styles.documentsView__moveSearchIcon}>
                            <SearchSmallIcon />
                        </div>
                        <input
                            className={styles.documentsView__moveSearchInput}
                            placeholder="Введите название проекта"
                            value={moveSearch}
                            onChange={(event) => setMoveSearch(event.target.value)}
                        />
                    </div>
                    <div className={styles.documentsView__moveList}>
                        {isMoveProjectsLoading ? (
                            <div className={styles.documentsView__moveEmpty}>Загружаем проекты...</div>
                        ) : filteredMoveProjects.length === 0 ? (
                            <div className={styles.documentsView__moveEmpty}>Проекты не найдены</div>
                        ) : (
                            filteredMoveProjects.map((project) => (
                                <button
                                    key={project.id}
                                    type="button"
                                    className={styles.documentsView__moveItem}
                                    onClick={() => handleMoveToProject(project)}
                                    disabled={isMoving}
                                >
                                    <div className={styles.documentsView__moveItemColor} style={{ borderColor: project.color }} />
                                    <span>{project.name}</span>
                                </button>
                            ))
                        )}
                    </div>
                </div>
            )}

            <ProjectDeleteModal
                isOpen={!!deleteDocument}
                title="Удалить документ?"
                text={`Вы уверены, что хотите удалить документ «${deleteDocument ? getProjectDocumentName(deleteDocument) : ''}»? Это действие нельзя отменить.`}
                onClose={() => {
                    if (!isDeleting) {
                        setDeleteDocument(null)
                    }
                }}
                onConfirm={confirmDelete}
            />
        </div>
    )
}
